import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL, formatEuri } from '../config/constants';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';

const KATEGORIJE_TROSKOVA = [
  'Građevinski radovi i kooperanti',
  'Građevinski materijal i oprema',
  'Zemljište, komunalije i doprinosi',
  'Projektiranje, nadzor i atesti',
  'Dozvole, javni bilježnici i pravne usluge',
  'Plaće, prehrana i terenski dodaci',
  'Porezi, banke, marketing i opći troškovi',
  'Ostali troškovi'
];

const ITEMS_PER_PAGE = 20;

export default function ProjectExpenses() {
  const { userInfo } = useAuth();
  const { selectedProjectID } = useProject();

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtri i Paginacija
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Modal za unos i uređivanje
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [saving, setSaving] = useState(false);

  const loggedInUserName = userInfo?.fullName || 'Korisnik';
  const loggedInUserID = userInfo?.userID || 3;
  const loggedInCompanyID = userInfo?.companyID || 1;

  const [formData, setFormData] = useState({
    category: KATEGORIJE_TROSKOVA[0],
    vendorName: '',
    description: '',
    amount: '',
    expenseDate: new Date().toISOString().substring(0, 10),
    invoiceNumber: '',
    paymentReference: ''
  });

  // Funkcija za automatski upis u aktivnosti (Audit Log / Chatter)
  const saveActivity = async (type, body) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        companyID: String(loggedInCompanyID),
        userID: String(loggedInUserID),
        type: type,
        body: body
      });

      await fetch(`${API_BASE_URL}/activityInsertRecord?${params.toString()}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Greška pri upisu aktivnosti:', error);
    }
  };

  // ESC Listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (isModalOpen) {
          setIsModalOpen(false);
        } else if (searchTerm) {
          setSearchTerm('');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategoryFilter]);

  // Dohvat stvarnih troškova
  const fetchExpenses = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const pID = selectedProjectID || 'all';

    try {
      const res = await fetch(`${API_BASE_URL}/projectExpenseList?projectID=${pID}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        setExpenses(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Greška pri dohvatu stvarnih troškova:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [selectedProjectID]);

  const handleOpenAdd = () => {
    setEditingExpense(null);
    setFormData({
      category: KATEGORIJE_TROSKOVA[0],
      vendorName: '',
      description: '',
      amount: '',
      expenseDate: new Date().toISOString().substring(0, 10),
      invoiceNumber: '',
      paymentReference: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (exp) => {
    setEditingExpense(exp);
    setFormData({
      category: exp.category || KATEGORIJE_TROSKOVA[0],
      vendorName: exp.vendorName || '',
      description: exp.description || '',
      amount: exp.amount !== null && exp.amount !== undefined ? exp.amount : '',
      expenseDate: exp.expenseDate ? exp.expenseDate.substring(0, 10) : new Date().toISOString().substring(0, 10),
      invoiceNumber: exp.invoiceNumber || '',
      paymentReference: exp.paymentReference || ''
    });
    setIsModalOpen(true);
  };

  // Spremanje troška + automatski Activity Log
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vendorName.trim() || !formData.amount) {
      alert('Molimo unesite naziv dobavljača/primatelja i iznos.');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');

    const payload = {
      ...formData,
      projectID: selectedProjectID && selectedProjectID !== 'Sve' ? selectedProjectID : null
    };

    if (editingExpense) {
      payload.expenseID = editingExpense.expenseID;
    }

    const endpoint = editingExpense 
      ? `${API_BASE_URL}/projectExpenseUpdateRecord` 
      : `${API_BASE_URL}/projectExpenseInsertRecord`;

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        // UPIS U AKTIVNOSTI
        const desc = editingExpense
          ? `${loggedInUserName} - ažuriran trošak "${formData.vendorName}" (${formatEuri(formData.amount)})`
          : `${loggedInUserName} - unesen novi trošak "${formData.vendorName}" (${formatEuri(formData.amount)})`;

        await saveActivity(editingExpense ? 'expense_update' : 'expense_insert', desc);

        setIsModalOpen(false);
        fetchExpenses();
      } else {
        alert('Greška pri spremanju troška.');
      }
    } catch (err) {
      console.error(err);
      alert('Greška pri spremanju.');
    } finally {
      setSaving(false);
    }
  };

  // Brisanje troška + automatski Activity Log
  const handleDelete = async () => {
    if (!editingExpense) return;
    if (!window.confirm(`Želite li obrisati trošak za "${editingExpense.vendorName}" (${formatEuri(editingExpense.amount)})?`)) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}/projectExpenseDeleteRecord?expenseID=${editingExpense.expenseID}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        // UPIS U AKTIVNOSTI
        await saveActivity(
          'expense_delete',
          `${loggedInUserName} - obrisan trošak "${editingExpense.vendorName}" (${formatEuri(editingExpense.amount)})`
        );

        setIsModalOpen(false);
        fetchExpenses();
      } else {
        alert('Greška pri brisanju.');
      }
    } catch (err) {
      console.error(err);
      alert('Greška pri brisanju.');
    }
  };

  const filteredExpenses = useMemo(() => {
    return expenses.filter((exp) => {
      const text = [
        exp.vendorName || '',
        exp.description || '',
        exp.invoiceNumber || '',
        exp.paymentReference || ''
      ].join(' ').toLowerCase();

      const matchesSearch = text.includes(searchTerm.toLowerCase());
      const matchesCat = selectedCategoryFilter === 'all' || exp.category === selectedCategoryFilter;
      return matchesSearch && matchesCat;
    });
  }, [expenses, searchTerm, selectedCategoryFilter]);

  const totalItems = filteredExpenses.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
  const currentExpenses = filteredExpenses.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleExportCSV = () => {
    if (filteredExpenses.length === 0) {
      alert('Nema troškova za izvoz.');
      return;
    }

    let csv = '\uFEFFDatum;Kategorija;Primatelj / Dobavljač;Opis;Iznos (€);Broj računa;Poziv na broj\r\n';
    filteredExpenses.forEach((e) => {
      csv += `"${e.expenseDate}";"${e.category}";"${e.vendorName.replace(/"/g, '""')}";"${(e.description || '').replace(/"/g, '""')}";"${parseFloat(e.amount || 0).toFixed(2)}";"${e.invoiceNumber || ''}";"${e.paymentReference || ''}"\r\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Troskovi_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalSum = expenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  const filteredSum = filteredExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

  return (
    <div className="max-w-6xl space-y-6 pb-12">
      
      {/* 1. ZAGLAVLJE */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Troškovi projekta</h1>
          <p className="text-xs text-slate-500 mt-0.5">Dnevnik svih stvarnih isplata i računa s bankovnog izvoda</p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handleOpenAdd}
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-xs flex items-center gap-1.5 cursor-pointer"
          >
            <span className="text-base leading-none">+</span> Dodaj trošak
          </button>

          <span className="text-xs font-bold bg-white border border-slate-200 px-3 py-2 rounded-xl text-slate-600 shadow-2xs">
            Ukupno: <strong className="text-slate-900">{totalItems}</strong>
          </span>
        </div>
      </div>

      {/* 2. KPI KARTICE */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ukupno isplaćeno</span>
          <p className="text-2xl font-extrabold text-slate-900 mt-1">{formatEuri(totalSum)}</p>
          <p className="text-xs text-slate-400 font-medium mt-1">Sve evidentirane isplate</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Broj plaćenih računa</span>
          <p className="text-2xl font-extrabold text-indigo-600 mt-1">{expenses.length}</p>
          <p className="text-xs text-slate-500 font-medium mt-1">Stavke s bankovnog izvoda</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Filtrirani zbroj</span>
          <p className="text-2xl font-extrabold text-slate-800 mt-1">{formatEuri(filteredSum)}</p>
          <p className="text-xs text-slate-400 font-medium mt-1">Prikazano po odabiru</p>
        </div>
      </div>

      {/* 3. UNIFICIRANA TRAKA ZA PRETRAGU I FILTER KATEGORIJE */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="relative w-full md:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
            search
          </span>
          <input
            type="text"
            placeholder="Pretraži po primatelju, opisu ili računu..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3.5 py-2 text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-slate-400 transition"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <select
            value={selectedCategoryFilter}
            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            className="bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl pl-3 pr-8 py-2 text-xs font-semibold text-slate-700 cursor-pointer focus:bg-white focus:outline-none transition"
          >
            <option value="all">Sve kategorije troškova</option>
            {KATEGORIJE_TROSKOVA.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleExportCSV}
            className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold text-xs px-3.5 py-2 rounded-xl transition cursor-pointer flex items-center gap-1.5 shadow-2xs"
          >
            <span className="material-symbols-outlined text-sm text-slate-500">download</span> Izvoz u CSV
          </button>
        </div>
      </div>

      {/* 4. TABLICA STVARNIH TROŠKOVA */}
      {loading ? (
        <p className="text-slate-400 text-center py-16 text-xs font-semibold">Učitavanje troškova...</p>
      ) : filteredExpenses.length === 0 ? (
        <div className="bg-white p-16 rounded-2xl border border-slate-200 shadow-xs text-center">
          <p className="text-xs text-slate-400 font-medium">Nema pronađenih troškova prema unesenom filteru.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-600 min-w-[720px]">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-400 font-bold text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="py-3.5 px-4 whitespace-nowrap">Datum</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Primatelj / Dobavljač</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Kategorija</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Opis plaćanja</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Iznos (€)</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-semibold">
                {currentExpenses.map((exp) => (
                  <tr
                    key={exp.expenseID}
                    onClick={() => handleOpenEdit(exp)}
                    className="hover:bg-slate-50/80 transition cursor-pointer group"
                  >
                    <td className="py-3.5 px-4 text-slate-500 font-medium whitespace-nowrap">
                      {exp.expenseDate}
                    </td>
                    <td className="py-3.5 px-4 text-slate-900 font-bold group-hover:text-indigo-600 transition whitespace-nowrap">
                      {exp.vendorName}
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                        {exp.category}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-slate-600 text-[11px] max-w-xs truncate">
                      {exp.description || '—'}
                    </td>
                    <td className="py-3.5 px-4 text-right font-extrabold text-slate-900 whitespace-nowrap">
                      {formatEuri(exp.amount)}
                    </td>
                    <td className="py-3.5 px-4 text-right text-slate-300 group-hover:text-slate-700 transition font-bold text-sm whitespace-nowrap">
                      ✎
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 5. PAGINACIJA */}
          {totalItems > 0 && (
            <div className="p-4 bg-slate-50/60 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <div className="text-slate-500 font-medium">
                Prikazano <span className="font-bold text-slate-900">{startIndex + 1}–{endIndex}</span> od{' '}
                <span className="font-bold text-slate-900">{totalItems}</span> troškova
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs cursor-pointer flex items-center gap-1"
                >
                  <span className="text-sm leading-none">‹</span> Prethodna
                </button>

                <div className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 font-bold text-slate-800 shadow-2xs">
                  {currentPage} / {totalPages}
                </div>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs cursor-pointer flex items-center gap-1"
                >
                  Sljedeća <span className="text-sm leading-none">›</span>
                </button>
              </div>
            </div>
          )}

        </div>
      )}

      {/* 6. MODAL ZA UNOS / UREĐIVANJE TROŠKA */}
      {isModalOpen && (
        <div
          onClick={() => setIsModalOpen(false)}
          className="fixed inset-0 bg-slate-900/60 z-[90] flex items-center justify-center p-4 backdrop-blur-sm cursor-pointer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto cursor-default text-left"
          >
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900">
                {editingExpense ? 'Uredi trošak' : 'Dodaj novi trošak'}
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1 cursor-pointer font-bold text-sm"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Kategorija troška (*)</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer"
                >
                  {KATEGORIJE_TROSKOVA.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Primatelj / Dobavljač (*)</label>
                <input
                  type="text"
                  required
                  placeholder="npr. Brodomerkur d.d."
                  value={formData.vendorName}
                  onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Iznos (€) (*)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Datum plaćanja (*)</label>
                  <input
                    type="date"
                    required
                    value={formData.expenseDate}
                    onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Opis plaćanja</label>
                <textarea
                  rows="2"
                  placeholder="npr. Plaćanje armature za 2. kat..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Broj računa</label>
                  <input
                    type="text"
                    placeholder="npr. RN 551-101"
                    value={formData.invoiceNumber}
                    onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Poziv na broj</label>
                  <input
                    type="text"
                    placeholder="npr. HR00 384-101"
                    value={formData.paymentReference}
                    onChange={(e) => setFormData({ ...formData, paymentReference: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center pt-3 border-t border-slate-100 gap-2">
                {editingExpense ? (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="text-xs font-bold text-rose-600 hover:text-rose-800 transition cursor-pointer"
                  >
                    Obriši trošak
                  </button>
                ) : <div />}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold text-slate-700 cursor-pointer"
                  >
                    Odustani
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold cursor-pointer shadow-xs disabled:opacity-50"
                  >
                    {saving ? 'Spremanje...' : editingExpense ? 'Spremi izmjene' : 'Spremi trošak'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}